import axios from "axios";
import md5 from "md5";
import Cookies from "js-cookie";
import { Auth } from "aws-amplify";

import {
	captureError,
	addApiBreadcrumb,
	logApiErrorToSentry,
} from "../services/util";

import { getIdTokenJwt } from "../auth/tokens";   // ✅ new unified token helper

export const rawAxios = axios.create();

export const userIdFromCookies = Cookies.get("userEmail")
	? md5(Cookies.get("userEmail").toLowerCase().trim())
	: "";

// --------------------------------------------------
// 👉 Helper: Get current Amplify user details
// --------------------------------------------------
async function getCurrentUser() {
	try {
		return await Auth.currentAuthenticatedUser({ bypassCache: false });
	} catch (err) {
		return null;
	}
}

// --------------------------------------------------
// REQUEST INTERCEPTOR
// --------------------------------------------------
axios.interceptors.request.use(
	async (config) => {
		try {
			// Attach fresh ID token
			const idToken = await getIdTokenJwt();
			if (idToken) {
				config.headers["Authorization"] = `Bearer ${idToken}`;
			}

			// Attach user email cookie (kept from previous logic)
			const user = await getCurrentUser();
			if (user?.attributes?.email) {
				Cookies.set("userEmail", user.attributes.email);
			}

			return config;
		} catch (err) {
			captureError(err, { tags: { phase: "request" } });
			return Promise.reject(err);
		}
	},
	(error) => {
		captureError(error, { tags: { phase: "request" } });
		return Promise.reject(error);
	}
);

// --------------------------------------------------
// RESPONSE INTERCEPTOR
// --------------------------------------------------
axios.interceptors.response.use(
	(response) => {
		if (response?.data?.error || response?.data?.statusCode >= 400) {
			const error = new Error(
				response?.data?.errorMessage ||
				response?.data?.error ||
				response?.data?.message ||
				response?.data?.message?.message ||
				"Something went wrong!"
			);

			logApiErrorToSentry(error, {
				status: response?.status,
				url: response?.config?.url,
				method: response?.config?.method,
				requestData: response?.config?.data,
				responsePayload: response?.data,
			});

			throw error;
		}

		return response;
	},
	(error) => {
		addApiBreadcrumb(error);

		if (error.response) {
			// typeof error?.response?.data === "string" => API returned plain text error message *
			// * only for certhub API currently
			error.message = typeof error?.response?.data === "string"
				? error?.response?.data
				:
				error?.response?.data?.error || "Something went wrong!";

			if ([401, 403, 500].includes(error?.response?.status)) {
				logApiErrorToSentry(error, {
					status: error?.response?.status,
					url: error?.config?.url,
					method: error?.config?.method,
					requestData: error?.config?.data,
					responsePayload: error?.response?.data,
				});
			}
		} else {
			captureError(error, {
				tags: {
					service: "axios",
					type: "network_error",
				},
			});
		}

		return Promise.reject(error);
	}
);

export default axios;
